import React, { createContext, useEffect, useMemo } from "react";

import { CompanyOverride, useCookieWatcher, useStorageCompanyOverride } from "@/hooks";
import { GetSelfResponse } from "@/lib/dto";
import { useSelfQuery } from "@/queries";

type CurrentCompany = CompanyOverride;

type AuthContextValue = {
  isLoggedIn: boolean;
  isError: boolean;
  fetchingPermissions: boolean;
  user: GetSelfResponse | undefined;
  companyContext: {
    currentCompany: CurrentCompany | undefined;
    isCompanyOverridden: boolean;
    overrideCompany: (data: CurrentCompany) => void;
    resetOverrideCompany: () => void;
  };
};
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

type AuthContextProviderProps = {
  children: React.ReactNode;
};
export const AuthContextProvider = (props: AuthContextProviderProps) => {
  const { children } = props;
  const isLoggedIn = useCookieWatcher("id_token");
  const selfQuery = useSelfQuery(isLoggedIn);

  const [storage, setStorage, clearStorage] = useStorageCompanyOverride();
  const selfData = selfQuery.data;
  const ownCompany = useMemo(
    () => selfData && { id: selfData.companyId, name: selfData.companyName },
    [selfData],
  );
  // The company picked in this session, otherwise the user's own
  const currentCompany: CurrentCompany | undefined = storage ?? ownCompany;

  // Always reset company override when user is no longer logged in.
  useEffect(() => {
    if (!isLoggedIn) {
      clearStorage();
    }
  }, [isLoggedIn, clearStorage]);

  return (
    <AuthContext.Provider
      value={{
        user: selfQuery.data,
        isError: selfQuery.isError,
        isLoggedIn,
        fetchingPermissions: selfQuery.isFetching,
        companyContext: {
          currentCompany,
          isCompanyOverridden: !!storage?.id,
          overrideCompany: setStorage,
          resetOverrideCompany: clearStorage,
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
